/**
 * Transactions: the player ledger (currency_ledger) — every Shroom and Coin
 * movement, deposits and withdrawals included — read from GET /admin/transactions,
 * with an .xlsx export of everything matching the current filter.
 *
 * "Import transactions" opens the same import panel as Admin → Invoices
 * (transaction-import.ts), so it can be found from either tab; an import reloads the list.
 */
import { el, clear } from '../../dom';
import { adminApi, type AdminTransactionPage } from '../../game-api';
import { fmtDateTime, fmtNum } from './format';
import { TX_TYPES, formatTxAmount, txTypeLabel, txUserLabel } from './transaction-format';
import { importPanel } from './transaction-import';
import { apiMessage, btn, emptyBox, errorBox, input, panel, pill, saveBlob, spinner, table, toastError } from './ui';

/** MAD's page size for this tab. */
export const TX_PAGE_SIZE = 25;
/** Same typing delay as the Players and Purchases searches. */
export const TX_SEARCH_DEBOUNCE_MS = 300;
export const TX_EXPORT_FILENAME = 'transactions-export.xlsx';

export function renderTransactions(host: HTMLElement): () => void {
  let cancelled = false;
  let seq = 0;
  const state = { page: 1, type: '', search: '' };

  const search = input('Search by player, e-mail or description…');
  const typeSelect = el('select',
    'px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold',
    { title: 'Filter by type' },
    el('option', '', { value: '' }, 'All types'),
    ...TX_TYPES.map((t) => el('option', '', { value: t }, txTypeLabel(t))));
  const exportBtn = btn('Export .xlsx', () => void exportXlsx(), 'primary');
  const listHost = el('div', 'min-h-[80px]');
  const pager = el('div', 'flex items-center justify-between gap-2 mt-3 flex-wrap');

  // A new filter or search always starts again from page 1.
  let timer: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => { state.search = search.value.trim(); state.page = 1; void load(); }, TX_SEARCH_DEBOUNCE_MS);
  });
  typeSelect.addEventListener('change', () => { state.type = typeSelect.value; state.page = 1; void load(); });

  async function load() {
    const mine = ++seq;
    clear(listHost); clear(pager);
    listHost.appendChild(spinner());
    try {
      const page = await adminApi.transactions({
        page: state.page, per_page: TX_PAGE_SIZE, type: state.type, search: state.search,
      });
      if (cancelled || mine !== seq) return;
      renderPage(page);
    } catch (e) {
      if (cancelled || mine !== seq) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load transactions."), () => void load()));
    }
  }

  async function exportXlsx() {
    exportBtn.disabled = true;
    const original = exportBtn.textContent;
    exportBtn.textContent = 'Exporting…';
    try {
      const blob = await adminApi.exportTransactions({ type: state.type, search: state.search });
      saveBlob(blob, TX_EXPORT_FILENAME);
    } catch (e) {
      toastError(e, 'Export failed');
    } finally {
      exportBtn.disabled = false;
      exportBtn.textContent = original;
    }
  }

  function renderPage(page: AdminTransactionPage) {
    const rows = page.transactions ?? [];
    clear(listHost); clear(pager);
    if (!rows.length) { listHost.appendChild(emptyBox('No transactions found.')); return; }

    listHost.appendChild(table(
      ['User', 'Type', 'Amount', 'Description', 'Date'],
      rows.map((tx) => [
        txUserLabel(tx),
        pill(tx.type || '—', 'mute'),
        el('span', `font-mono ${tx.delta < 0 ? 'text-error' : 'text-leaf'}`, {}, formatTxAmount(tx.currency, tx.delta)),
        el('span', 'whitespace-normal break-words text-text-dim', {}, tx.description || '—'),
        fmtDateTime(tx.createdAt),
      ])));

    const pages = Math.max(1, page.pages || 1);
    const current = page.page || state.page;
    pager.append(
      el('div', 'text-xs text-text-dim font-mono', {}, `${fmtNum(page.total ?? rows.length)} transactions`),
      el('div', 'flex gap-2 items-center', {},
        pageBtn('Prev', current > 1, () => { state.page = current - 1; void load(); }),
        el('span', 'text-xs text-text-dim font-mono', {}, `Page ${current} / ${pages}`),
        pageBtn('Next', current < pages, () => { state.page = current + 1; void load(); })));
  }

  const importHost = el('div');
  const toggleImport = () => {
    if (importHost.firstChild) { clear(importHost); return; }
    importHost.appendChild(importPanel(() => { state.page = 1; void load(); }, () => clear(importHost)));
  };

  host.appendChild(panel('Transactions',
    el('div', 'flex flex-wrap items-center gap-2 mb-3', {},
      el('div', 'flex-1 min-w-[180px]', {}, search), typeSelect, btn('Import transactions', toggleImport), exportBtn),
    importHost,
    listHost, pager));
  void load();
  return () => { cancelled = true; clearTimeout(timer); };
}

function pageBtn(label: string, enabled: boolean, onClick: () => void): HTMLButtonElement {
  const b = btn(label, () => { if (enabled) onClick(); });
  if (!enabled) { b.disabled = true; b.className += ' opacity-40 cursor-not-allowed'; }
  return b;
}
